import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { LocalStorageService } from './local-storage.service';
import { ApiClientHelper } from '../helpers/api-client.helper';
import { AppConfig } from '../../shared/config/app-config';
import { StringHelper } from '../helpers/strings.helper';
import { StorageKeys } from '../../shared/models/storage-keys';
import { NibssVerificationPaths } from '../routes/nibss-bvn-verfication.routes';
import {
    NibssBvnFlow,
    NibssBvnVerificationData,
    NibssBvnVerificationReferenceResponse,
    NibssBvnVerificationResponse,
    NibssBvnVerificationStatus,
} from '../models/nibss-bvn-verfication.model';

@Injectable({
    providedIn: 'root',
})
export class NibssVerificationService {
    constructor(
        private apiClientHelper: ApiClientHelper,
        private localStorage: LocalStorageService,
        private router: Router,
        public config: AppConfig,
    ) {}

    canUseNibssBvnVerification() {
        return this.config.USE_NIBSS_BVN_VERIFICATION;
    }

    initialize() {
        this.clearIsBrmFlow();
    }

    validateBVN(data): Promise<NibssBvnVerificationResponse> {
        const url = this.config.MONIEPOINT_ONBOARDING + `/api/v2/business-owner-onboarding/level-1/self-onboarding/bvn/validation`;
        return this.apiClientHelper.postForPromise(url, data);
    }

    setNibssFlow(flow: NibssBvnFlow) {
        if (!flow) {
            this.localStorage.deleteValue('Nibss-flow');
            return;
        }
        this.localStorage.save('Nibss-flow', flow);
    }

    getNibssFlow() {
        return this.localStorage.get('Nibss-flow') ?? NibssBvnFlow.PROFILE_CREATION;
    }

    handleNibssRouting(data: NibssBvnVerificationResponse) {
        if (data.shouldRedirect) {
            const url = data?.redirectUrl;
            this.navigateToNibssVerificationPortal(url);
            return;
        }
        this.routeToVerifyReference(data.reference);
    }

    verifyReference(reference: string): Promise<NibssBvnVerificationReferenceResponse> {
        const url =
            this.config.MONIEPOINT_ONBOARDING +
            `/api/v1/business-owner-onboarding/level-1/self-onboarding/bvn-reference/validation?reference=${reference}`;
        return this.apiClientHelper.postForPromise(url, {});
    }

    navigateToNibssVerificationPortal(url: string) {
        window.open(url, '_self');
    }

    redirectToFailedModal() {
        return this.router.navigateByUrl(NibssVerificationPaths.FailedVerificationModal);
    }

    redirectToSuccessModal() {
        return this.router.navigateByUrl(NibssVerificationPaths.SuccessfulVerificationModal);
    }

    redirectToStartVerification() {
        return this.router.navigateByUrl(NibssVerificationPaths.Start);
    }

    routeToVerifyReference(reference: string) {
        return this.router.navigateByUrl(
            `${NibssVerificationPaths.Redirect}?ref=${reference}&status=${NibssBvnVerificationStatus.SUCCESS.toLowerCase()}`,
        );
    }

    routeToHome() {
        return this.router.navigate(['']);
    }

    retryBvnVerification() {
        return this.redirectToStartVerification();
    }

    getNibssVerificationData(): NibssBvnVerificationData | null {
        const data = this.localStorage.get(StorageKeys.NIBSS_VERIFICATION_COMPONENT_DATA);
        if (StringHelper.isNullOrEmptyOrUndefined(data)) {
            return;
        }
        return JSON.parse(data);
    }

    setNibssVerificationData(data: NibssBvnVerificationData) {
        if (StringHelper.isNullOrEmptyOrUndefined(data)) {
            this.clearNibssVerificationComponentData();
            return;
        }
        this.localStorage.save(StorageKeys.NIBSS_VERIFICATION_COMPONENT_DATA, JSON.stringify(data));
    }

    clearNibssVerificationComponentData() {
        this.localStorage.deleteValue(StorageKeys.NIBSS_VERIFICATION_COMPONENT_DATA);
    }

    clearRedirectUrl() {
        this.localStorage.deleteValue(StorageKeys.NIBSS_VERIFICATION_REDIRECT_URL);
    }

    saveBvnValidationKey(key: string) {
        if (StringHelper.isNullOrEmptyOrUndefined(key)) {
            this.clearBvnValidationKey();
            return;
        }
        this.localStorage.save(StorageKeys.NIBSS_VERIFICATION_BVN_VALIDATION_KEY, key);
    }

    getBvnValidationKey() {
        return this.localStorage.get(StorageKeys.NIBSS_VERIFICATION_BVN_VALIDATION_KEY);
    }

    clearBvnValidationKey() {
        this.localStorage.deleteValue(StorageKeys.NIBSS_VERIFICATION_BVN_VALIDATION_KEY);
    }

    getIsBrmFlow() {
        const result = this.localStorage.get(StorageKeys.NIBSS_VERIFICATION_IS_BRM_FLOW);
        return result && result === 'true';
    }

    setIsBrmFlow(isBrmFlow: boolean) {
        if (StringHelper.isNullOrEmptyOrUndefined(isBrmFlow)) {
            this.clearRedirectUrl();
            return;
        }
        this.localStorage.save(StorageKeys.NIBSS_VERIFICATION_IS_BRM_FLOW, isBrmFlow);
    }

    clearIsBrmFlow() {
        this.localStorage.deleteValue(StorageKeys.NIBSS_VERIFICATION_IS_BRM_FLOW);
    }

    goBack() {
        this.redirectToFailedNibssVerificationRoute();
        this.clearRedirectUrl();
        this.clearNibssVerificationComponentData();
        this.clearBvnValidationKey();
    }

    completeNibssVerification() {
        this.redirectToSuccessfulNibssVerificationRoute();
        this.clearRedirectUrl();
        this.clearNibssVerificationComponentData();
    }

    private redirectToSuccessfulNibssVerificationRoute() {
        const data = this.getNibssVerificationData();
        if (data?.shouldUpdateStep && data?.storageKey) {
            this.setNextStep(data.nextSteps, data.storageKey as string);
        }
        const route = data?.successfulRedirectRoute || data.redirectRoute;
        this.router.navigateByUrl(route);
        return;
    }

    private redirectToFailedNibssVerificationRoute() {
        const data = this.getNibssVerificationData();
        const route = data?.failedRedirectRoute || data.redirectRoute;
        this.router.navigateByUrl(route);
        return;
    }

    private setNextStep(step, key?: string) {
        if (key && step) {
            this.localStorage.save(key, step);
        }
    }
}
